import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    ManagedActionModal,
    ManagedActionModalMode,
    ManagedActionModalState,
    ManagedApiError,
    buildManagedActionModalState,
} from '../Shared/ManagedHelpers';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Form,
    Input,
    Row,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import Administration from '../../Constants/Endpoints/Administration';
import CreateReportRequest from '../../Interfaces/Requests/Administration/CreateReportRequest';
import UpdateReportRequest from '../../Interfaces/Requests/Administration/UpdateReportRequest';
import GetReportAdminModelResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelResponse';

type ReportProps = {
    id: number;
    name: string;
    groupName: string;
    isDashboard: boolean;
    useDatasetInputs: boolean;
    released: boolean;
    apiOnly: boolean;
    exportExcel: boolean;
    exportPDF: boolean;
    exportPassword: string;
    newReport: boolean;
    close: () => void;
    refetch: () => void;
};

const Report: React.FC<ReportProps> = ({ ...props }) => {
    const queryClient = useQueryClient();

    const [reportName, setReportName] = useState(props.name);
    const [reportGroupName, setReportGroupName] = useState(props.groupName);
    const [isDashboard, setIsDashBoard] = useState(props.isDashboard);
    const [useDatasetInputs, setUseDatasetInputs] = useState(props.useDatasetInputs);
    const [released, setReleased] = useState(props.released);
    const [apiOnly, setAPIOnly] = useState(props.apiOnly);
    const [exportExcel, setExportExcel] = useState(props.exportExcel);
    const [exportPDF, setExportPDF] = useState(props.exportPDF);
    const [exportPassword, setExportPassword] = useState(props.exportPassword);
    const [submitDisabled, setSubmitDisabled] = useState(true);

    const reinstateValues = useCallback(() => {
        setReportName(props?.name);
        setReportGroupName(props?.groupName);
        setIsDashBoard(props?.isDashboard);
        setUseDatasetInputs(props?.useDatasetInputs);
        setReleased(props?.released);
        setAPIOnly(props?.apiOnly);
        setExportExcel(props?.exportExcel);
        setExportPDF(props?.exportPDF);
        setExportPassword(props?.exportPassword);
    }, [
        props?.name,
        props?.groupName,
        props?.isDashboard,
        props?.useDatasetInputs,
        props?.released,
        props?.apiOnly,
        props?.exportExcel,
        props?.exportPDF,
        props?.exportPassword
    ]);

    const [formCreUpdReport] = Form.useForm();

    const [currentError, setCurrentError] = useState('');

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const showReportInfoModal = (
        title: string,
        message: string,
        isError: boolean = true,
        mode: ManagedActionModalMode = 'info'
    ) => {
        setManageActionModalState(buildManagedActionModalState(mode, title, message, isError));
    };

    const getReportActionModalMode = (): ManagedActionModalMode => {
        return props.newReport ? 'add' : 'update';
    };

    const handleManageActionModalOk = () => {
        const shouldCloseAfterDismiss = manageActionModalState.open && !manageActionModalState.isError;

        setManageActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));

        if (shouldCloseAfterDismiss) {
            props.close();
            props.refetch();
        }
    };

    const handleReportFinishFailed = () => {
        showReportInfoModal(
            props.newReport ? 'Create Report Error' : 'Update Report Error',
            'The Report form could not be submitted.  Review the form and try again.',
            true,
            getReportActionModalMode()
        );
    };

    useEffect(() => {
        reinstateValues();
        formCreUpdReport.setFieldValue('creUpdReportName', props?.name);
        formCreUpdReport.setFieldValue('creUpdReportGroupName', props?.groupName);
        formCreUpdReport.setFieldValue('creUpdExportPassword', props?.exportPassword);
    }, [
        reinstateValues,
        formCreUpdReport,
        props?.name,
        props?.groupName,
        props?.isDashboard,
        props?.useDatasetInputs,
        props?.released,
        props?.apiOnly,
        props?.exportExcel,
        props?.exportPDF,
        props?.exportPassword,
    ]);

    const handleSubmitDisabled = useCallback(() => {
        if (reportName && reportGroupName) {
            setSubmitDisabled(false);
        } else {
            setSubmitDisabled(true);
        }
    }, [reportName, reportGroupName]);

    useEffect(() => {
        handleSubmitDisabled();
    }, [handleSubmitDisabled, reportName, reportGroupName]);

    const handleReportNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setReportName(aValue.target.value);
        handleSubmitDisabled();
    };
    const handleReportGroupNameChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setReportGroupName(aValue.target.value);
        handleSubmitDisabled();
    };
    const handleIsDashboardChange = (aValue: { target: { checked: boolean } }) => {
        setIsDashBoard(aValue.target.checked);
    };
    const handleUseDatasetInputsChange = (aValue: { target: { checked: boolean } }) => {
        setUseDatasetInputs(aValue.target.checked);
    };
    const handleReleasedChange = (aValue: { target: { checked: boolean } }) => {
        setReleased(aValue.target.checked);
    };
    const handleAPIOnlyChange = (aValue: { target: { checked: boolean } }) => {
        setAPIOnly(aValue.target.checked);
    };
    const handleExportExcelChange = (aValue: { target: { checked: boolean } }) => {
        setExportExcel(aValue.target.checked);
    };
    const handleExportPDFChange = (aValue: { target: { checked: boolean } }) => {
        setExportPDF(aValue.target.checked);
    };
    const handleExportPasswordChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setExportPassword(aValue.target.value);
    };

    const onFinishCreUpdReport = () => {
        if (props.newReport) {
            creReport();
        } else {
            updReport();
        }
    };

    const { mutate: creReport, isLoading: isCreReportLoading, isError: isCreReportError, error: creReportError, } = useMutation(
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.CREATE_REPORT_REQUEST}`;
                const requestData: CreateReportRequest = {
                    name: reportName,
                    groupName: reportGroupName,
                    isDashboard: isDashboard,
                    useDatasetInputs: useDatasetInputs,
                    released: released,
                    apiOnly: apiOnly,
                    exportExcel: exportExcel,
                    exportPDF: exportPDF,
                    exportPassword: exportPassword,
                };
                const responseData = await AxiosUtil.post<GetReportAdminModelResponse, CreateReportRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('CREATE_REPORT_REQUEST: [Create Report] Internal Error: ' + err);
                console.error('CREATE_REPORT_REQUEST: [Create Report] Internal Error: ', err);
                showReportInfoModal(
                    'Create Report Error',
                    `Create Report: ${reportGroupName}: ${reportName} Failed. Please Contact Application Support`,
                    true,
                    'add'
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('CREATE_REPORT_REQUEST: [Create Report] Internal Error: ' + err);
                console.error('CREATE_REPORT_REQUEST: [Create Report] Internal Error: ', err);
                showReportInfoModal(
                    'Create Report Error',
                    `Create Report: ${reportGroupName}: ${reportName} Failed. Please Contact Application Support`,
                    true,
                    'add'
                );
            },
            onSuccess: (response: GetReportAdminModelResponse) => {
                invalidateReportMenu(queryClient);

                showReportInfoModal(
                    'Create Report',
                    `Create Report: ${reportGroupName}: ${reportName} Succeeded.`,
                    false,
                    'add'
                );
            },
        }
    );

    const { mutate: updReport, isLoading: isUpdReportLoading, isError: isUpdReportError, error: updReportError, } = useMutation(
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.UPD_REPORT_REQUEST}`;
                const requestData: UpdateReportRequest = {
                    id: props.id,
                    name: reportName,
                    groupName: reportGroupName,
                    isDashboard: isDashboard,
                    useDatasetInputs: useDatasetInputs,
                    released: released,
                    apiOnly: apiOnly,
                    exportExcel: exportExcel,
                    exportPDF: exportPDF,
                    exportPassword: exportPassword,
                };
                const responseData = await AxiosUtil.post<GetReportAdminModelResponse, UpdateReportRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('UPD_REPORT_REQUEST: [Upd Report] Internal Error: ' + err);
                console.error('UPD_REPORT_REQUEST: [Upd Report] Internal Error: ', err);
                showReportInfoModal(
                    'Update Report Error',
                    `Update Report: ${reportGroupName}: ${reportName} Failed. Please Contact Application Support`,
                    true,
                    'update'
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('UPD_REPORT_REQUEST: [Upd Report] Internal Error: ' + err);
                console.error('UPD_REPORT_REQUEST: [Upd Report] Internal Error: ', err);
                showReportInfoModal(
                    'Update Report Error',
                    `Update Report: ${reportGroupName}: ${reportName} Failed. Please Contact Application Support`,
                    true,
                    'update'
                );
            },
            onSuccess: (response: GetReportAdminModelResponse) => {
                invalidateReportMenu(queryClient);

                showReportInfoModal(
                    'Update Report',
                    `Update Report: ${reportGroupName}: ${reportName} Succeeded.`,
                    false,
                    'update'
                );
            },
        }
    );

    const handleCancel = () => {
        reinstateValues();
        props.close();
    };

    const getReportActionColor = (): string => {
        return props.newReport ? 'cornflowerblue' : 'darkgray';
    };

    const determineWhichError = () => {
        if ( isCreReportError ) {
            return currentError + ' ' + ((creReportError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isUpdReportError ) {
            return currentError + ' ' + ((updReportError as ManagedApiError).response?.data?.message ?? '');
        }
        else {
            return '';
        }
    };

    return (
        <>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageActionModalOk}
                onCancel={handleManageActionModalOk}
            />
            <Spin spinning={
                            isCreReportLoading ||
                            isUpdReportLoading
                            }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isCreReportError ||
                        isUpdReportError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: getReportActionColor(), borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>{props.newReport ? 'Create New Report' : 'Update Existing Report'}</Title>
                    </Text>
                </Space>
                <Form
                    form={formCreUpdReport}
                    key='reportdetails'
                    onFinish={onFinishCreUpdReport}
                    onFinishFailed={handleReportFinishFailed}
                >
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cureportname'
                                    label='Report Name'
                                    labelCol={{ span: 7, }}
                                    name='creUpdReportName'
                                    rules={[
                                        {
                                            message: 'Report Name Required.',
                                            required: true,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={reportName}
                                        onChange={handleReportNameChange}
                                        placeholder='Enter Report Name'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='cureportgroupname'
                                    label='Report Group Name'
                                    labelCol={{ span: 7, }}
                                    name='creUpdReportGroupName'
                                    rules={[
                                        {
                                            message: 'Report Group Name Required.',
                                            required: true,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={reportGroupName}
                                        onChange={handleReportGroupNameChange}
                                        placeholder='Enter Report Group Name'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Checkbox
                                    checked={isDashboard}
                                    onChange={handleIsDashboardChange}
                                >
                                    Dashboard
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Checkbox
                                    checked={useDatasetInputs}
                                    onChange={handleUseDatasetInputsChange}
                                >
                                    Use Dataset Inputs
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Checkbox
                                    checked={released}
                                    onChange={handleReleasedChange}
                                >
                                    Released
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Checkbox
                                    checked={apiOnly}
                                    onChange={handleAPIOnlyChange}
                                >
                                    API Only
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Checkbox
                                    checked={exportExcel}
                                    onChange={handleExportExcelChange}
                                >
                                    Export Excel
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Checkbox
                                    checked={exportPDF}
                                    onChange={handleExportPDFChange}
                                >
                                    Export PDF
                                </Checkbox>
                            </Col>
                        </Row>
                        <Row style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <Form.Item
                                    key='crexportpassword'
                                    label='Export Password'
                                    labelCol={{ span: 7, }}
                                    name='creUpdExportPassword'
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        value={exportPassword}
                                        onChange={handleExportPasswordChange}
                                        placeholder='Enter Export Password'
                                    ></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col span={16} />
                            <Col span={4}>
                                <Form.Item>
                                    <Button
                                        disabled={submitDisabled}
                                        htmlType='submit'
                                        style={{backgroundColor: getReportActionColor(), float: 'right'}}
                                        type='primary'
                                    >
                                        Submit
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Button onClick={handleCancel}>Cancel</Button>
                            </Col>
                        </Row>
                    </div>
                </Form>
            </Spin>
        </>
    );
};

export default Report;
